import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DOCUMENT } from '../constants/documentTheme';
import PartnerLockup from './PartnerLockup';
import PartnerLogoMark from './PartnerLogoMark';
import { isUsableImageUrl } from '../utils/itineraryItemImage';
import { isUsableTripLocationName } from '../utils/tripUtils';

const TripBookedBy = ({
  name,
  logoUrl,
  banner,
  onClick,
  variant = 'header',
  labelKey = 'trip.bookedByLabel',
  action,
}) => {
  const { t } = useTranslation();
  if (!isUsableTripLocationName(name)) return null;
  const isCover = variant === 'cover';
  const hasPartnerLogo = isUsableImageUrl(logoUrl);
  const markSize = isCover ? 28 : 40;
  const plate = /^#[0-9a-fA-F]{6}$/.test(String(banner || '')) ? banner : '#000000';

  return (
    <Box
      data-testid="trip-booked-by-row"
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        columnGap: 2.5,
        rowGap: 1.25,
        mt: isCover ? 0 : 1.75,
        flex: isCover ? '1 1 100%' : undefined,
        maxWidth: '100%',
      }}
    >
      <Box
        component={onClick ? 'button' : 'div'}
        type={onClick ? 'button' : undefined}
        onClick={onClick}
        data-testid="trip-booked-by"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: isCover ? 1 : 1.5,
          minWidth: 0,
          p: 0,
          border: 0,
          bgcolor: 'transparent',
          color: DOCUMENT.ivory,
          cursor: onClick ? 'pointer' : 'default',
          font: 'inherit',
          textAlign: 'left',
          '&:hover .trip-booked-by-name': onClick ? { textDecoration: 'underline' } : undefined,
        }}
      >
        {hasPartnerLogo ? (
          <PartnerLockup
            src={logoUrl}
            alt={name}
            banner={banner}
            height={markSize}
            maxWidth={isCover ? 148 : 200}
            sx={isCover ? { px: 1, py: 0.5 } : undefined}
          />
        ) : (
          <PartnerLogoMark
            partner={{ company_name: name }}
            alt={name}
            sx={{
              width: markSize,
              height: markSize,
              flexShrink: 0,
              bgcolor: plate,
              color: DOCUMENT.ivory,
              fontSize: isCover ? 11 : 14,
              fontWeight: 600,
            }}
          />
        )}
        <Box
          data-testid={isCover ? 'trip-booked-by-label' : undefined}
          sx={{
            minWidth: 0,
            ...(isCover ? {
              bgcolor: 'rgba(18, 5, 8, 0.82)',
              px: 1,
              py: 0.5,
            } : {}),
          }}
        >
          <Typography
            sx={{
              fontSize: isCover ? 9 : 10,
              letterSpacing: '0.19em',
              textTransform: 'uppercase',
              color: isCover ? DOCUMENT.ivory : 'rgba(246,239,228,0.72)',
              mb: 0.25,
            }}
          >
            {t(labelKey)}
          </Typography>
          <Typography
            className="trip-booked-by-name"
            sx={{
              fontSize: isCover ? 12 : 15,
              color: DOCUMENT.ivory,
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {name}
          </Typography>
        </Box>
      </Box>
      {action ? (
        <Box
          data-testid="trip-booked-by-action"
          sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' }, minWidth: 0, maxWidth: '100%' }}
        >
          {action}
        </Box>
      ) : null}
    </Box>
  );
};

export default TripBookedBy;
